import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { useMe } from '@/auth/useAuth';
import { useParentCourseContext } from '@/auth/parentCourseContext';
import { CourseMarkdown } from '@/components/CourseMarkdown';

export function ParentStudioBanner() {
  const location = useLocation();
  const parts = location.pathname.split('/');
  const projectId = ['playground', 'blocks', 'code', 'video'].includes(parts[2])
    ? parts[3]
    : undefined;
  const me = useMe('user');
  const ownerId = me.data?.kind === 'user' ? me.data.sub : null;
  const project = useQuery({
    queryKey: ['project-back', projectId, 'user', ownerId],
    queryFn: () =>
      api<{ owner_user_id: string | null; kid_id: string | null }>(
        `/projects/${encodeURIComponent(projectId!)}`,
        { principal: 'user' },
      ),
    enabled: !!ownerId && !!projectId && projectId !== 'new',
  });
  const own =
    !projectId ||
    projectId === 'new' ||
    (!!ownerId && project.data?.owner_user_id === ownerId && !project.data.kid_id);
  const context = useParentCourseContext(projectId, own);
  return (
    <aside
      className="shrink-0 border-b border-hairline bg-wash-mint px-3 py-2 text-sm sm:px-5"
      aria-label="Parent workspace identity"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <strong className="text-ink">Parent workspace</strong>
        <a href="/portal" className="font-bold underline underline-offset-4">
          Portal home
        </a>
        <a href="/portal/projects" className="font-bold underline underline-offset-4">
          My projects
        </a>
        <span className="text-ink-soft">
          {own
            ? location.pathname.startsWith('/learn/create/image')
              ? 'Your pictures belong to your parent account. Drawing drafts stay on this device.'
              : 'Your work saves to your parent account.'
            : project.isSuccess
              ? 'You are editing your child’s project. Changes stay with that child.'
              : project.isError
                ? 'Project ownership could not load. Check before editing.'
                : 'Checking project owner…'}
        </span>
      </div>
      {context.data && (
        <p className="mt-1 truncate text-ink-soft">
          {context.data.title} · {context.data.lesson_title} · {context.data.mission_title}
        </p>
      )}
      <details className="mt-1">
        <summary className="cursor-pointer font-semibold text-ink">
          Lesson and working together
        </summary>
        <div className="max-h-48 overflow-y-auto py-3 pr-2">
          <p className="text-ink-soft">
            Take turns discussing and testing. The current project keeps its owner. To switch to
            your child’s account, save and leave first, then use their login. AI help uses your
            family’s Stars.
          </p>
          {context.isError && (
            <p role="alert" className="mt-3">
              Course guidance could not load. Use Home to return to your course, or retry here.
            </p>
          )}
          {context.isError && (
            <button className="btn-pill-secondary mt-2" onClick={() => void context.refetch()}>
              Retry lesson guidance
            </button>
          )}
          {context.data?.content_md && (
            <div className="mt-3">
              <CourseMarkdown text={context.data.content_md} />
            </div>
          )}
          <p className="mt-3 font-semibold text-ink">
            Check the workspace save status before leaving. Its Home or Back control returns you to
            your course or saved projects.
          </p>
        </div>
      </details>
    </aside>
  );
}
